if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

const ensureFullMenuHiddenStyle = (() => {
  let ready = false;
  return () => {
    if (ready) return;
    ready = true;
    const style = document.createElement('style');
    style.id = 'fct-full-menu-hidden-style';
    style.textContent = `
      .thb-full-menu > li.fct-full-menu-hidden {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  };
})();

const hideOverflowMenuItems = (menu) => {
  if (!menu || menu.offsetParent === null) return;
  const items = Array.from(menu.children || []).filter((item) => item && item.tagName === 'LI');
  if (!items.length) return;

  items.forEach((item) => {
    item.classList.remove('fct-full-menu-hidden');
  });

  const tolerance = 2;
  const getVisibleItems = () => items.filter((item) => !item.classList.contains('fct-full-menu-hidden') && item.offsetParent);
  const getRowTops = (visibleItems) => {
    const tops = visibleItems.map((item) => Math.round(item.offsetTop));
    return Array.from(new Set(tops)).sort((a, b) => a - b);
  };
  let guard = 0;
  while (guard < items.length) {
    const visibleItems = getVisibleItems();
    if (!visibleItems.length) break;

    const rowTops = getRowTops(visibleItems);
    if (rowTops.length <= 1) break;
    const lastRowTop = rowTops[rowTops.length - 1];
    const lastVisible = visibleItems.findLast((item) => Math.round(item.offsetTop) >= lastRowTop - tolerance) || visibleItems[visibleItems.length - 1];

    lastVisible.classList.add('fct-full-menu-hidden');
    guard += 1;
  }
};

const transferFourthNavItem = (header) => {
  const leftMenu = header.querySelector('.columns > full-menu > .thb-full-menu');
  const rightMenu = header.querySelector('.thb-header-right full-menu > .thb-full-menu');
  if (!leftMenu || !rightMenu) return;

  const item = header.fourthNavItem || leftMenu.children[3];
  if (!item) return;
  header.fourthNavItem = item;

  // Measure in its original position so widening the header restores the item.
  if (item.parentElement !== leftMenu) leftMenu.appendChild(item);
  item.classList.remove('fct-full-menu-hidden');
  if (leftMenu.offsetParent === null) return;

  hideOverflowMenuItems(leftMenu);
  if (item.classList.contains('fct-full-menu-hidden')) {
    item.classList.remove('fct-full-menu-hidden');
    // The secondary list uses row-reverse: appending places it before Community.
    rightMenu.appendChild(item);
  }
};
/**
 *  @class
 *  @function ThemeHeader
 */
if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = document.querySelector('.mobile-toggle-wrapper');
      this.mobileQuery = window.matchMedia('(max-width: 767px)');
      this.announcementBar = document.querySelector('.announcement-bar') || document.getElementById('shopify-section-announcement-bar');
      this.headerEl = this.querySelector('.header') || this.header_section?.querySelector('.header');
      this.lastMobileHeaderOffset = null;
      this.cachedAnnouncementHeight = null;
      this.cachedHeaderHeight = null;
      this.cachedHeaderOffset = null;
      this.rafScheduled = false;

      document.addEventListener('keyup', (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === 'ESCAPE') {
            this.toggle.removeAttribute('open');
            this.toggle.classList.remove('active');
          }
        }
      });
      this.toggle?.querySelector('.mobile-toggle')?.addEventListener('click', (e) => {
        if (this.toggle.classList.contains('active')) {
          e.preventDefault();
          document.body.classList.remove('overflow-hidden');
          this.toggle.classList.remove('active');
          this.closeAnimation(this.toggle);
        } else {
          document.body.classList.add('overflow-hidden');
          setTimeout(() => {
            this.toggle.classList.add('active');
          });
        }
        window.dispatchEvent(new Event('resize.resize-select'));
      });

      // Sticky Header Class
      this.handleScroll = () => {
        this.setStickyClass();
        this.scheduleUpdate();
      };
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      // Layout metrics
      this.handleResize = debounce(() => {
        this.measureHeights(true);
        this.updateLayoutMetrics();
        this.setStickyClass();
      }, 50);
      window.addEventListener('resize', this.handleResize, {
        passive: true
      });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.measureHeights(true);
          this.updateLayoutMetrics();
        });
        if (this.header_section) this.resizeObserver.observe(this.header_section);
        if (this.announcementBar) this.resizeObserver.observe(this.announcementBar);
      }

      this.updateLayoutMetrics();
      this.setStickyClass();

      if (document.querySelector('.announcement-bar-section')) {
        window.addEventListener('resize', this.setAnnouncementHeight.bind(this), {
          passive: true
        });
        this.setAnnouncementHeight();
      }

      // Buttons.
      if (this.menu) {
        this.menu.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
        this.menu.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
      }
    }

    scheduleUpdate() {
      if (this.rafScheduled) return;
      this.rafScheduled = true;
      requestAnimationFrame(() => {
        this.rafScheduled = false;
        this.updateLayoutMetrics();
      });
    }

    measureHeights(force = false) {
      const announcementHeight = this.announcementBar ? this.announcementBar.getBoundingClientRect().height : 0;
      const headerHeight = this.getBoundingClientRect().height;
      if (force || announcementHeight !== this.cachedAnnouncementHeight) {
        this.cachedAnnouncementHeight = announcementHeight;
      }
      if (force || headerHeight !== this.cachedHeaderHeight) {
        this.cachedHeaderHeight = headerHeight;
      }
    }

    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        const headerSection = this.header_section;
        const sectionPosition = headerSection ? window.getComputedStyle(headerSection).position : '';
        let isSticky = false;

        if (sectionPosition === 'fixed' || this.mobileQuery?.matches) {
          isSticky = window.scrollY > 0;
        } else {
          let offset = parseInt(this.getBoundingClientRect().top, 10) + document.documentElement.scrollTop;
          isSticky = window.scrollY >= offset && window.scrollY > 0;
        }

        this.classList.toggle('is-sticky', isSticky);
        const headerEl = this.headerEl || this.querySelector('.header');
        if (headerEl) {
          headerEl.classList.toggle('is-sticky', isSticky);
        }
      }
    }
    setAnnouncementHeight() {
      const h = this.cachedAnnouncementHeight ?? (this.announcementBar ? this.announcementBar.getBoundingClientRect().height : 0);
      document.documentElement.style.setProperty('--announcement-height', h + 'px');
    }
    setHeaderOffset() {
      if (!this.header_section) {
        return;
      }
      let h = this.header_section.getBoundingClientRect().top;
      if (this.cachedHeaderOffset !== h) {
        this.cachedHeaderOffset = h;
        document.documentElement.style.setProperty('--header-offset', h + 'px');
      }
    }
    setHeaderHeight() {
      const h = this.cachedHeaderHeight ?? this.clientHeight;
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }
    updateMobileMenuOffset() {
      if (!this.mobileQuery?.matches) {
        return;
      }
      const announcementHeight = this.cachedAnnouncementHeight ?? (this.announcementBar ? this.announcementBar.getBoundingClientRect().height : 0);
      if (this.header_section) {
        this.header_section.style.top = `${announcementHeight}px`;
      }
      const headerHeight = this.cachedHeaderHeight ?? this.getBoundingClientRect().height;
      const isAtTop = window.scrollY <= 8;
      const headerEl = this.headerEl || this.querySelector('.header') || this.header_section?.querySelector('.header');
      const isTransparent = headerEl ? headerEl.classList.contains('transparent--true') : this.classList.contains('transparent--true');
      const menuOffset = Math.max(0, Math.round(announcementHeight + headerHeight));
      const headerOffset = (isAtTop && isTransparent) ? Math.max(0, Math.round(announcementHeight)) : menuOffset;

      document.documentElement.style.setProperty('--fct-mobile-menu-offset', `${menuOffset}px`);
      document.documentElement.style.setProperty('--fct-mobile-header-offset', `${headerOffset}px`);
      if (this.menu) {
        this.menu.style.setProperty('--fct-mobile-menu-offset', `${menuOffset}px`);
      }

      this.lastMobileHeaderOffset = headerOffset;
    }
    updateLayoutMetrics() {
      this.measureHeights();
      this.setAnnouncementHeight();
      this.setHeaderOffset();
      this.setHeaderHeight();
      this.updateMobileMenuOffset();
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');
      const isOpen = detailsElement.hasAttribute('open');

      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }

      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}
/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.menu = this.querySelector('.thb-full-menu');
      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');

      const updateOverflow = () => {
        ensureFullMenuHiddenStyle();
        const transferHeader = this.closest('[data-transfer-fourth-nav-item]');
        if (transferHeader) {
          transferFourthNavItem(transferHeader);
        } else {
          hideOverflowMenuItems(this.menu);
        }
      };
      // resize on initial load
      this.handleResize = debounce(() => {
        this.resizeSubMenus();
        updateOverflow();
      }, 100);
      window.addEventListener('resize', this.handleResize);

      document.fonts?.ready?.then(() => {
        this.resizeSubMenus();
        updateOverflow();
      });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.resizeSubMenus();
          updateOverflow();
        });
      });

      if (this.menu && window.ResizeObserver) {
        this.menuResizeObserver = new ResizeObserver(updateOverflow);
        this.menuResizeObserver.observe(this.menu);
      }
    }
    resizeSubMenus() {
      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');
      if (!this.submenus.length) {
        return;
      }
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.parentElement.clientWidth + 10,
            total = w + l;
          if (total > document.body.clientWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}
